import { useState, useEffect, useCallback } from 'react';
import './HeroSection.css';
import PaperViewer from './PaperViewer';
import PaperCalendar from './PaperCalendar';
import MonthlyPaperPicker from './MonthlyPaperPicker';
import PollSection from './PollSection';
import { useLang } from '../context/LanguageContext';
import { useAuth, hasFullAccess } from '../context/AuthContext';
import { useSubscriptionGate } from '../context/SubscriptionGateContext';
import { useNewsData } from '../context/NewsDataContext';
import {
  ensureDailyPaperAccess,
  ensureMonthlyPaperAccess,
  getCachedMonthlyAccess,
  warmDailyPaper,
  warmMonthlyPaper,
  getPdfBytes,
  clearDailyPaperAccess,
  OPEN_DAILY_PAPER_EVENT,
  OPEN_MONTHLY_PAPER_EVENT,
} from '../utils/paperCache';

import { API_URL as API } from '../config/env.js';
const LOCALE_MAP = { te: 'te-IN', en: 'en-IN', ta: 'ta-IN', kn: 'kn-IN' };

function formatMonthLabel(monthKey, lang) {
  const [y, m] = monthKey.split('-');
  const locale = LOCALE_MAP[lang] || 'te-IN';
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(locale, {
    month: 'long',
    year: 'numeric',
  });
}

export default function HeroSection({ openEditionDate = null }) {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const { homeFeed } = useNewsData();
  const { openSubscribe } = useSubscriptionGate();
  const fullAccess = hasFullAccess(user);
  const [activeTab, setActiveTab] = useState('daily');
  const [showCalendar, setShowCalendar] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLabel, setPdfLabel] = useState('');
  const [previewMaxPages, setPreviewMaxPages] = useState(null);
  const [resolvingPaper, setResolvingPaper] = useState(false);
  const [paperError, setPaperError] = useState('');
  const [latestDaily, setLatestDaily] = useState(null);
  const [latestMonthly, setLatestMonthly] = useState(null);

  const todayDisplay = new Date().toLocaleDateString(LOCALE_MAP[lang] || 'te-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const isDaily = activeTab === 'daily';
  const latestKey = isDaily ? latestDaily : latestMonthly;

  useEffect(() => {
    if (homeFeed.latestPaperDate) {
      setLatestDaily(homeFeed.latestPaperDate);
    }
  }, [homeFeed.latestPaperDate]);

  useEffect(() => {
    fetch(`${API}/api/papers`)
      .then((r) => r.json())
      .then((d) => {
        if (d.dates?.length) setLatestDaily((prev) => prev || d.dates[0]);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${API}/api/monthly-papers`)
      .then((r) => r.json())
      .then((d) => {
        if (d.months?.length) setLatestMonthly(d.months[0]);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!latestDaily) return;
    warmDailyPaper(API, user?.token, latestDaily, { preview: !user || !fullAccess });
  }, [user?.token, latestDaily, fullAccess, user]);

  useEffect(() => {
    if (!user?.token || !latestMonthly || !fullAccess) return;
    warmMonthlyPaper(API, user.token, latestMonthly);
  }, [user?.token, latestMonthly, fullAccess]);

  const formatDailyLabel = useCallback(
    (dateKey) =>
      new Date(dateKey + 'T00:00:00').toLocaleDateString(LOCALE_MAP[lang] || 'te-IN', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }),
    [lang]
  );

  const openDailyPaper = useCallback(
    async (dateKey, { preview = false } = {}) => {
      const isLatest = dateKey === latestDaily;
      const usePreview = preview || (!fullAccess && isLatest) || (!user && isLatest);
      if (!user && !isLatest) {
        window.location.href = `/login?next=${encodeURIComponent(window.location.pathname)}`;
        return;
      }

      setViewerOpen(true);
      setPaperError('');
      setPdfLabel(formatDailyLabel(dateKey));
      setResolvingPaper(true);
      // Clear previous URL so the viewer doesn't keep a stale PDF while today's loads.
      setPdfUrl(null);
      setPreviewMaxPages(null);

      try {
        let access = await ensureDailyPaperAccess(API, user?.token, dateKey, { preview: usePreview });
        // Wait for PDF bytes before revealing — avoids blank/error on first open of today's edition.
        try {
          await getPdfBytes(access.pdfUrl);
        } catch {
          clearDailyPaperAccess(dateKey, usePreview);
          access = await ensureDailyPaperAccess(API, user?.token, dateKey, {
            preview: usePreview,
            force: true,
          });
          await getPdfBytes(access.pdfUrl);
        }
        setPreviewMaxPages(access.maxPages);
        setPdfUrl(access.pdfUrl);
      } catch (err) {
        setViewerOpen(false);
        if (err?.code === 'LOGIN') {
          window.location.href = `/login?next=${encodeURIComponent(window.location.pathname)}`;
          return;
        }
        if (err?.code === 'SUBSCRIBE' || err?.status === 403) {
          openSubscribe();
          return;
        }
        setPaperError(t.paperNotAvailable);
      } finally {
        setResolvingPaper(false);
      }
    },
    [formatDailyLabel, t.paperNotAvailable, user?.token, user, fullAccess, latestDaily, openSubscribe]
  );

  useEffect(() => {
    if (openEditionDate && latestDaily === openEditionDate) {
      openDailyPaper(openEditionDate);
    }
  }, [openEditionDate, latestDaily, openDailyPaper]);

  useEffect(() => {
    const onOpenFromToast = (event) => {
      const dateKey = event.detail?.dateKey;
      if (!dateKey) return;
      setActiveTab('daily');
      openDailyPaper(dateKey, { preview: !fullAccess });
    };
    window.addEventListener(OPEN_DAILY_PAPER_EVENT, onOpenFromToast);
    return () => window.removeEventListener(OPEN_DAILY_PAPER_EVENT, onOpenFromToast);
  }, [openDailyPaper, fullAccess]);

  const openMonthlyPaper = useCallback(
    async (monthKey) => {
      if (!user?.token) return;
      const cached = getCachedMonthlyAccess(monthKey);
      setViewerOpen(true);
      setPaperError('');
      if (cached) {
        setPdfLabel(formatMonthLabel(monthKey, lang));
        setPdfUrl(cached.pdfUrl);
        setResolvingPaper(false);
        return;
      }
      setResolvingPaper(true);
      setPdfUrl(null);
      setPreviewMaxPages(null);
      try {
        const access = await ensureMonthlyPaperAccess(API, user.token, monthKey);
        setPdfLabel(formatMonthLabel(monthKey, lang));
        setPdfUrl(access.pdfUrl);
      } catch (err) {
        setViewerOpen(false);
        if (err?.code === 'SUBSCRIBE' || err?.status === 403) {
          openSubscribe();
          return;
        }
        setPaperError(t.paperNotAvailable);
      } finally {
        setResolvingPaper(false);
      }
    },
    [lang, t.paperNotAvailable, user?.token, openSubscribe]
  );

  useEffect(() => {
    const onOpenMonthly = (event) => {
      const monthKey = event.detail?.monthKey;
      if (!monthKey) return;
      setActiveTab('monthly');
      openMonthlyPaper(monthKey);
    };
    window.addEventListener(OPEN_MONTHLY_PAPER_EVENT, onOpenMonthly);
    return () => window.removeEventListener(OPEN_MONTHLY_PAPER_EVENT, onOpenMonthly);
  }, [openMonthlyPaper]);

  const warmCurrentPaper = useCallback(() => {
    if (!user?.token || !latestKey) return;
    if (isDaily) warmDailyPaper(API, user.token, latestKey, { preview: !fullAccess });
    else if (fullAccess) warmMonthlyPaper(API, user.token, latestKey);
  }, [user?.token, latestKey, isDaily, fullAccess]);

  const handleReadClick = () => {
    if (!latestKey) return;
    if (isDaily) openDailyPaper(latestKey);
    else openMonthlyPaper(latestKey);
  };

  const handlePreviewClick = () => {
    if (!latestDaily) return;
    openDailyPaper(latestDaily, { preview: true });
  };

  const closePaper = () => {
    setViewerOpen(false);
    setPdfUrl(null);
    setPreviewMaxPages(null);
    setResolvingPaper(false);
  };

  const handleDateSelect = (dateKey) => {
    setShowCalendar(false);
    openDailyPaper(dateKey);
  };

  const handleMonthSelect = (monthKey) => {
    setShowCalendar(false);
    openMonthlyPaper(monthKey);
  };

  return (
    <section className="hero-section" id="paper">
      {viewerOpen && (
        <PaperViewer
          onClose={closePaper}
          pdfPath={pdfUrl}
          waitingForPath={resolvingPaper}
          dateLabel={pdfLabel}
          maxPages={previewMaxPages}
          onPreviewSubscribe={openSubscribe}
        />
      )}
      {showCalendar && isDaily && (
        <PaperCalendar onSelectDate={handleDateSelect} onClose={() => setShowCalendar(false)} />
      )}
      {showCalendar && !isDaily && (
        <MonthlyPaperPicker onSelectMonth={handleMonthSelect} onClose={() => setShowCalendar(false)} />
      )}

      <div className="today-paper-card">
        <div className="paper-bg-pattern" />
        <div className="paper-inner">
          <div className="paper-tabs" data-sub-free>
            <button
              type="button"
              className={`paper-tab ${isDaily ? 'active' : ''}`}
              onClick={() => setActiveTab('daily')}
            >
              📰 {t.dailyEdition || 'దినపత్రిక'}
            </button>
            <button
              type="button"
              className={`paper-tab ${!isDaily ? 'active' : ''}`}
              onClick={() => setActiveTab('monthly')}
            >
              📖 {t.monthlyEdition || 'నెలవారీ మ్యాగజైన్'}
            </button>
          </div>

          <div className="paper-masthead">
            <div className="paper-masthead-row">
              <div className="paper-masthead-logo">{t.brandName}</div>
              <time className="paper-masthead-date" dateTime={new Date().toISOString().slice(0, 10)}>
                {todayDisplay}
              </time>
            </div>
            <div className="paper-masthead-divider" />
          </div>

          <h2 className="paper-headline">
            {isDaily ? t.paperHeadline : t.monthlyHeadline || 'Monthly Magazines'}
          </h2>
          <p className="paper-sub">{t.paperSub}</p>

          {paperError && (
            <p className="paper-error" role="alert">
              {paperError}
            </p>
          )}

          <div className="paper-actions">
            <button
              className="paper-cta"
              onClick={handleReadClick}
              onMouseEnter={warmCurrentPaper}
              onTouchStart={warmCurrentPaper}
              disabled={resolvingPaper || !latestKey}
            >
              {resolvingPaper ? `⏳ ${t.loading}` : isDaily ? t.readDailyBtn : t.readMonthlyBtn}
            </button>
            <button type="button" className="paper-cal-btn" data-sub-free onClick={() => setShowCalendar(true)}>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              {isDaily ? t.browsePastDaily : t.browsePastMonthly}
            </button>
          </div>

          {isDaily && latestDaily && !fullAccess && (
            <div className="paper-preview-offer" data-sub-free>
              <div className="paper-preview-offer__pages" aria-hidden>
                <span className="paper-preview-offer__page paper-preview-offer__page--back" />
                <span className="paper-preview-offer__page paper-preview-offer__page--front">
                  <span className="paper-preview-offer__page-num">1</span>
                </span>
                <span className="paper-preview-offer__page paper-preview-offer__page--peek">
                  <span className="paper-preview-offer__page-num">2</span>
                </span>
              </div>
              <div className="paper-preview-offer__copy">
                <span className="paper-preview-offer__eyebrow">{t.previewEyebrow}</span>
                <strong className="paper-preview-offer__title">{t.previewTitle}</strong>
                <p className="paper-preview-offer__sub">{t.previewSub}</p>
              </div>
              <button
                type="button"
                className="paper-preview-offer__btn"
                onClick={handlePreviewClick}
                disabled={resolvingPaper}
              >
                {t.previewBtnAction}
              </button>
            </div>
          )}

          <div className="paper-inside" data-sub-free>
            <span className="paper-inside-label">
              <span className="paper-inside-ap">AP</span>
              {t.zonesLabel || 'AP Publication Centers'}
            </span>
            <div className="paper-inside-items">
              {(t.zones || []).map((z) => (
                <div key={z.name} className="paper-inside-item">
                  <span className="paper-inside-icon" aria-hidden>
                    {z.icon}
                  </span>
                  <span className="paper-inside-text">{z.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="paper-stats">
            <div className="paper-stat">
              <span className="paper-stat-val">5+</span>
              <span className="paper-stat-label">{t.yearsLabel || 'సంవత్సరాలు'}</span>
            </div>
            <div className="paper-stat-sep" />
            <div className="paper-stat">
              <span className="paper-stat-val">1200+</span>
              <span className="paper-stat-label">{t.issuesLabel || 'సంచికలు'}</span>
            </div>
            <div className="paper-stat-sep" />
            <div className="paper-stat">
              <span className="paper-stat-val">50K+</span>
              <span className="paper-stat-label">{t.readersLabel || 'పాఠకులు'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="hero-bottom" id="archive">
        <PollSection inline />
      </div>
    </section>
  );
}
